import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight, faFilter, faSpinner } from "@fortawesome/pro-solid-svg-icons";
import { useTranslation } from "react-i18next";
import React from "react";
import { Tooltip } from "@mui/material";

type DataGridRow = Record<string, unknown>;

type DataGridFooterPaginationProps = React.HTMLAttributes<HTMLDivElement> & {
    handleNextPage: () => void;
    handlePreviousPage: () => void;
    data: Array<string | number>;
    rows: string[];
    currentPage: number;
    pageSize: number;
    loading?: boolean;
    filtered?: boolean;
};

export default function DataGridFooterPagination({
    handleNextPage,
    handlePreviousPage,
    data,
    rows,
    currentPage,
    pageSize,
    loading,
    filtered,
    ...props
}: DataGridFooterPaginationProps) {
    const { t } = useTranslation();
    const totalRows = Array.isArray(data) ? data.length : (rows?.length || 0);

    const displayPage = currentPage + 1;

    const startRow = totalRows === 0 ? 0 : (currentPage * pageSize) + 1;
    const endRow = Math.min((currentPage + 1) * pageSize, totalRows);

    const paginationRange = totalRows === 0
        ? t('pagination', { startItem: "0", endItem: "0", totalItems: "0" })
        : t('pagination', { startItem: startRow, endItem: endRow, totalItems: totalRows });

    const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));

    return (
        <>
            <div {...props} className="flex items-center rounded-[0.4rem] overflow-hidden border border-stone-300 shadow-sm min-w-[69px]">
                <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 0 || loading}
                    aria-disabled={currentPage === 0}
                    tabIndex={currentPage === 0 ? -1 : 0}
                    className="px-3 py-2 bg-stone-500 text-white disabled:text-white/40 hover:bg-stone-600 disabled:hover:bg-stone-200 disabled:bg-stone-200 transition-colors disabled:cursor-default"
                >
                    <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <div className="px-4 py-2 bg-white text-stone-800 font-medium hidden sm:flex">
                    {displayPage} / {totalPages}
                </div>
                <button
                    onClick={handleNextPage}
                    disabled={displayPage === totalPages || totalRows === 0 || loading}
                    aria-disabled={displayPage === totalPages || totalRows === 0}
                    tabIndex={displayPage === totalPages || totalRows === 0 ? -1 : 0}
                    className="px-3 py-2 bg-stone-500 text-white disabled:text-white/40 hover:bg-stone-600 disabled:hover:bg-stone-200 disabled:bg-stone-200 transition-colors disabled:cursor-default"
                >
                    <FontAwesomeIcon icon={faChevronRight} />
                </button>
            </div>
            <div className="text-gray-700 text-sm space-x-2 flex items-center">
                <span>{paginationRange}</span>
                {filtered && (
                    <Tooltip title={t('cards:list:filtered') as string}>
                        <FontAwesomeIcon icon={faFilter} className="text-black/60 cursor-help" />
                    </Tooltip>
                )}
                {loading && (<FontAwesomeIcon icon={faSpinner} className="text-black/60 animate-spin" />)}
            </div>
        </>
    );
}
